/* eslint-disable i18next/no-literal-string */
// src/components/errors/ErrorDetailsDrawer.jsx
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Api as ApiIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { tokens } from '../../../theme';

export default function ErrorDetailsDrawer({
  open,
  error,
  errorDetails,
  loading = false,
  onClose,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could show a snackbar here
    });
  };

  const formatJsonData = (data) => {
    if (!data) return 'No data';
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600, md: 800 },
          maxWidth: '90vw',
        },
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="between" mb={3}>
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            <ErrorIcon color="error" />
            <Typography variant="h5" fontWeight="bold">
              Error Details
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={300} />
          </Box>
        ) : error && errorDetails ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Basic Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          Occurred
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <LocalizedDateDistance date={errorDetails.occurred_at} />
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(errorDetails.occurred_at).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CodeIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          Error Code
                        </Typography>
                      </Box>
                      <Chip
                        label={errorDetails.error_code}
                        color="error"
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ApiIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          HTTP Status
                        </Typography>
                      </Box>
                      <Chip
                        label={`${errorDetails.http_status_code} (${errorDetails.http_method})`}
                        color={errorDetails.http_status_code >= 500 ? 'error' : 'warning'}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ErrorIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          Exception Type
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        fontFamily="monospace"
                        color="error.main"
                      >
                        {errorDetails.exception_type}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Error Message */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                  <Typography variant="h6">
                    Error Message
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(errorDetails.error_message)}
                  >
                    Copy
                  </Button>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors.redAccent[900],
                    borderRadius: 1,
                    border: `1px solid ${colors.redAccent[700]}`,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: colors.redAccent[200],
                    wordBreak: 'break-word',
                  }}
                >
                  {errorDetails.error_message}
                </Box>
              </CardContent>
            </Card>

            {/* Endpoint Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Endpoint Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight="medium">
                        Endpoint
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: theme.palette.background.default,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={errorDetails.http_method} 
                            size="small" 
                            color="primary"
                            sx={{ fontFamily: 'monospace' }}
                          />
                          <Typography fontFamily="monospace">
                            {errorDetails.endpoint}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* User Context */}
            {(errorDetails.staff_member_id || errorDetails.department_id || errorDetails.client_ip) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Context
                  </Typography>
                  <Grid container spacing={2}>
                    {errorDetails.staff_member_id && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              Staff Member ID
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontFamily="monospace">
                            {errorDetails.staff_member_id}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    {errorDetails.department_id && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              Department ID
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontFamily="monospace">
                            {errorDetails.department_id}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    {errorDetails.client_ip && (
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight="medium">
                            Client IP
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace">
                            {errorDetails.client_ip}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    {errorDetails.user_agent && (
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight="medium">
                            User Agent
                          </Typography>
                          <Typography 
                            variant="body2" 
                            fontFamily="monospace"
                            sx={{ wordBreak: 'break-all' }}
                          >
                            {errorDetails.user_agent}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Request Data */}
            {errorDetails.request_data && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                    <Typography variant="h6">
                      Request Data
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(errorDetails.request_data)}
                    >
                      Copy
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      maxHeight: 400,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {formatJsonData(errorDetails.request_data)}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Stack Trace */}
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                  <Typography variant="h6">
                    Stack Trace
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(errorDetails.traceback)}
                  >
                    Copy
                  </Button>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors.grey[900],
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    maxHeight: 600,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    color: colors.grey[100],
                    '& .error-line': {
                      backgroundColor: colors.redAccent[900],
                      color: colors.redAccent[200],
                      padding: '2px 4px',
                      borderRadius: '2px',
                    },
                  }}
                >
                  {errorDetails.traceback}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box textAlign="center" py={6}>
            <ErrorIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No error selected
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}